
import React, { useState, useMemo } from 'react';
import { SubmissionData } from '../types';
import { SubmissionStatus, WorkflowStage, SubmissionType } from '../enums';
import { Plus, ChevronDown, Package, User, Calendar, FileText, AlertCircle, Edit, CornerUpLeft, Download, ImageIcon, CheckCircle, XCircle, Loader2, Check, Lock, Search, LayoutList, CalendarDays, Lightbulb, Tag, PieChart, TrendingUp, BarChart3 } from 'lucide-react';
import { asArray } from '../utils/asArray';
import { BrandChip } from './common/BrandChip';
import { SubmissionCode } from './common/SubmissionCode';

interface DashboardProps {
  submissions: SubmissionData[];
  onNew: () => void;
  onHome: () => void;
  onEdit: (submission: SubmissionData) => void;
  managerName: string;
  managerId?: string;
  canSubmit?: boolean;
  onViewSubmissions?: () => void; // New Prop
  onNewProposal?: () => void;
  focusSubmissionId?: string | null;
    onBack?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ submissions, onNew, onHome, onEdit, managerName, managerId, canSubmit = true, onViewSubmissions, onNewProposal, focusSubmissionId, onBack }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'month'>('list');
  const [statusTab, setStatusTab] = useState<'active' | 'completed' | 'rejected'>('active');
  const [typeTab, setTypeTab] = useState<'campaign' | 'proposal'>('campaign');
  const safeSubmissions = asArray(submissions).filter(Boolean) as SubmissionData[];
  const resolveBrandName = (sub: SubmissionData) => {
    const explicit = (sub as any).brandName || (sub as any).brand?.name;
    if (explicit) return explicit;
    const list = asArray((sub as any).brands)
      .map((b: any) => (typeof b === 'string' ? b : b?.name || b?.id))
      .find(Boolean);
    if (list) return list as string;
    const id = (sub as any).brandId;
    return id || 'Unknown brand';
  };
  const renderBrandChip = (sub: SubmissionData) => <BrandChip brandName={resolveBrandName(sub)} />;

  // Handle Deep Linking
  React.useEffect(() => {
    if (focusSubmissionId) {
        const target = safeSubmissions.find(s => s.submissionId === focusSubmissionId);
        if (target) {
            // Switch tabs to match the target
            if (target.type === SubmissionType.PROPOSAL) setTypeTab('proposal');
            else setTypeTab('campaign');

            if (target.status === SubmissionStatus.REJECTED) setStatusTab('rejected');
            else if (target.status === SubmissionStatus.APPROVED || target.status === SubmissionStatus.EXPORTED) setStatusTab('completed');
            else setStatusTab('active');

            setExpandedId(focusSubmissionId);
            
            // Scroll into view after a brief delay to allow rendering
            setTimeout(() => {
                const el = document.getElementById(`submission-${focusSubmissionId}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    }
  }, [focusSubmissionId, submissions]);

  const resolveImage = (img?: string | null) => {
    if (!img) return null;
    const value = img.trim();
    if (!value) return null;
    // R2 public URLs sometimes require auth; rewrite to our proxy so it stays public with CORS
    const r2Match = value.match(/^https?:\/\/[^/]*r2\.dev\/(.+)$/i);
    if (r2Match && r2Match[1]) {
      return `/api/assets?key=${encodeURIComponent(r2Match[1])}`;
    }
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('data:image')) {
      const commaIdx = value.indexOf(',');
      const payload = commaIdx >= 0 ? value.slice(commaIdx + 1).trim() : value;
      if (payload.startsWith('/api/assets?key=')) {
        const key = decodeURIComponent(payload.split('=')[1] || '');
        return `/api/assets?key=${encodeURIComponent(key)}`;
      }
      return value;
    }
    const key = value.startsWith('/api/assets?key=')
      ? decodeURIComponent(value.split('=')[1] || '')
      : value.replace(/^\/+/, '');
    return `/api/assets?key=${encodeURIComponent(key)}`;
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Only show submissions belonging to this manager
  const mySubmissions = submissions.filter(s => {
    if (managerId) return s.managerId === managerId || s.manager.fileNumber === managerId;
    return s.manager.name === managerName;
  });

  const renderAnalytics = () => {
      const total = mySubmissions.length;
      const campaigns = mySubmissions.filter(s => s.type !== SubmissionType.PROPOSAL).length;
      const proposals = mySubmissions.filter(s => s.type === SubmissionType.PROPOSAL).length;
      
      const approved = mySubmissions.filter(s => s.status === SubmissionStatus.APPROVED || s.status === SubmissionStatus.EXPORTED).length;
      const rejected = mySubmissions.filter(s => s.status === SubmissionStatus.REJECTED).length;
      const pending = mySubmissions.filter(s => s.status === SubmissionStatus.PENDING).length;
      const revision = mySubmissions.filter(s => s.status === SubmissionStatus.NEEDS_REVISION).length;

      const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;

      // Brands
      const brandCounts: Record<string, number> = {};
      mySubmissions.forEach(s => {
          s.brands.forEach(b => {
              brandCounts[b] = (brandCounts[b] || 0) + 1;
          });
      });
      const topBrands = Object.entries(brandCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 5);

      return (
          <div className="space-y-6 animate-fade-in">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                      <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-zinc-100 rounded-lg text-zinc-500"><FileText className="w-5 h-5" /></div>
                          <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Total Submissions</span>
                      </div>
                      <div className="text-3xl font-extrabold text-zinc-900">{total}</div>
                      <div className="text-xs text-zinc-500 mt-1">{campaigns} Campaigns • {proposals} Proposals</div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                      <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600"><CheckCircle className="w-5 h-5" /></div>
                          <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Approval Rate</span>
                      </div>
                      <div className="text-3xl font-extrabold text-zinc-900">{approvalRate}%</div>
                      <div className="text-xs text-zinc-500 mt-1">{approved} Approved</div>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                      <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-amber-50 rounded-lg text-amber-600"><CornerUpLeft className="w-5 h-5" /></div>
                          <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Needs Revision</span>
                      </div>
                      <div className="text-3xl font-extrabold text-zinc-900">{revision}</div>
                      <div className="text-xs text-zinc-500 mt-1">Action Required</div>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                      <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-red-50 rounded-lg text-red-600"><XCircle className="w-5 h-5" /></div>
                          <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Rejected</span>
                      </div>
                      <div className="text-3xl font-extrabold text-zinc-900">{rejected}</div>
                      <div className="text-xs text-zinc-500 mt-1">Total Rejected</div>
                  </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Status Breakdown */}
                  <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                      <h3 className="font-bold text-zinc-900 mb-6 flex items-center gap-2">
                          <PieChart className="w-5 h-5 text-zinc-400" /> Status Breakdown
                      </h3>
                      <div className="space-y-4">
                          {[
                              { label: 'Approved', count: approved, color: 'bg-emerald-500', bg: 'bg-emerald-50' },
                              { label: 'Pending Review', count: pending, color: 'bg-teal-500', bg: 'bg-teal-50' },
                              { label: 'Needs Revision', count: revision, color: 'bg-amber-500', bg: 'bg-amber-50' },
                              { label: 'Rejected', count: rejected, color: 'bg-red-500', bg: 'bg-red-50' }
                          ].map(item => (
                              <div key={item.label}>
                                  <div className="flex justify-between text-sm mb-1">
                                      <span className="font-medium text-zinc-700">{item.label}</span>
                                      <span className="font-bold text-zinc-900">{item.count}</span>
                                  </div>
                                  <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden">
                                      <div className={`h-full ${item.color}`} style={{ width: `${total > 0 ? (item.count / total) * 100 : 0}%` }}></div>
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>

                  {/* Top Brands */}
                  <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                      <h3 className="font-bold text-zinc-900 mb-6 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-zinc-400" /> Top Brands
                      </h3>
                      <div className="space-y-3">
                          {topBrands.map(([brand, count], idx) => (
                              <div key={brand} className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                                  <div className="flex items-center gap-3">
                                      <div className="w-6 h-6 rounded-full bg-white border border-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-500">
                                          {idx + 1}
                                      </div>
                                      <span className="font-bold text-zinc-700">{brand}</span>
                                  </div>
                                  <span className="px-2 py-1 bg-white rounded-lg text-xs font-bold text-zinc-600 border border-zinc-200 shadow-sm">
                                      {count} items
                                  </span>
                              </div>
                          ))}
                          {topBrands.length === 0 && (
                              <div className="text-center text-zinc-400 py-8 text-sm">No brand data available</div>
                          )}
                      </div>
                  </div>
              </div>
          </div>
      );
  };

  // Search Logic
  const filteredSubmissions = useMemo(() => {
    const base = asArray(mySubmissions).filter(Boolean) as SubmissionData[];
    return base.filter(sub => {
        // Filter by Type
        const isProposal = sub?.type === SubmissionType.PROPOSAL;
        if (typeTab === 'campaign' && isProposal) return false;
        if (typeTab === 'proposal' && !isProposal) return false;

        const term = searchTerm.toLowerCase();
        const matchesId = (sub.submissionId || '').toLowerCase().includes(term);
        const matchesCampaign = (sub.campaignName || '').toLowerCase().includes(term);
        const matchesItems = asArray(sub.items).some(i => (i?.name || '').toLowerCase().includes(term));
        return matchesId || matchesCampaign || matchesItems;
    });
  }, [mySubmissions, searchTerm, typeTab]);

  // Tab filtering + newest first
  const displayedSubmissions = useMemo(() => {
    const activeStatuses = [
      SubmissionStatus.PENDING,
      SubmissionStatus.NEEDS_REVISION,
      SubmissionStatus.REJECTED,
    ];
    const isCompleted = (status?: SubmissionStatus) =>
      status === SubmissionStatus.APPROVED || status === SubmissionStatus.EXPORTED;

    const filteredByTab = filteredSubmissions.filter((sub) => {
      const isRej = sub.status === SubmissionStatus.REJECTED;
      if (statusTab === 'rejected') return isRej;
      if (statusTab === 'completed') return isCompleted(sub.status);
      return !isCompleted(sub.status) && !isRej;
    });

    return filteredByTab.sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bTime - aTime;
    });
  }, [filteredSubmissions, statusTab]);

  // Grouping Logic
  const groupedSubmissions = useMemo(() => {
    if (viewMode === 'list') return null;
    
    const groups: Record<string, SubmissionData[]> = {};
    
    displayedSubmissions.forEach(sub => {
        let dateKey = 'Recent';
        if (sub.timestamp) {
            try {
                // Try to parse the timestamp
                const date = new Date(sub.timestamp);
                if (!isNaN(date.getTime())) {
                    dateKey = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                } else {
                    const parts = sub.timestamp.split('/');
                    if(parts.length >= 3) {
                         const d = new Date(parseInt(parts[2].split(',')[0]), parseInt(parts[0])-1, parseInt(parts[1]));
                         if (!isNaN(d.getTime())) dateKey = d.toLocaleString('default', { month: 'long', year: 'numeric' });
                    }
                }
            } catch (e) {
                // Ignore parse errors
            }
        }
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(sub);
    });

    return groups;
  }, [displayedSubmissions, viewMode]);


  const handleDownloadExcel = async (data: SubmissionData) => {
      const ExcelJS = (window as any).ExcelJS;
      if (!ExcelJS) { alert("Excel library not loaded."); return; }
      
      try {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Submission Data');

        sheet.columns = [
            { key: 'id', width: 10 }, 
            { key: 'name', width: 35 }, 
            { key: 'sprice', width: 15 }, 
            { key: 'desc', width: 40 }, 
            { key: 'platforms', width: 40 }, 
            { key: 'image', width: 30 },
        ];

        sheet.mergeCells('A1:F2');
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'STERLING DISCOUNT CAMPAIGN - SUBMISSION REPORT';
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        const metaStartRow = 4;
        sheet.getCell(`A${metaStartRow}`).value = 'SUBMISSION DETAILS';
        sheet.getCell(`A${metaStartRow}`).font = { size: 11, bold: true, color: { argb: 'FF0F766E' }, underline: true };
        
        const metaData = [
            ['Submission ID', data.submissionId],
            ['Date Generated', data.timestamp || new Date().toLocaleDateString()],
            ['Manager Name', data.manager.name],
            ['File Number', data.manager.fileNumber],
            ['Selected Brands', data.brands.join(', ')]
        ];

        let currentRow = metaStartRow + 1;
        metaData.forEach(([label, value]) => {
            const labelCell = sheet.getCell(`A${currentRow}`);
            labelCell.value = label;
            labelCell.font = { bold: true, color: { argb: 'FF71717A' } }; 
            const valCell = sheet.getCell(`B${currentRow}`);
            valCell.value = value;
            valCell.font = { bold: true };
            sheet.mergeCells(`B${currentRow}:D${currentRow}`);
            currentRow++;
        });

        currentRow += 2;
        const headerRowIdx = currentRow;
        const headers = ['Item #', 'Item Name', 'Original Price', 'Description', 'Target Platforms', 'Image Filename'];
        
        headers.forEach((text, idx) => {
            const colLetter = String.fromCharCode(65 + idx); 
            const cell = sheet.getCell(`${colLetter}${headerRowIdx}`);
            cell.value = text;
            cell.font = { bold: true, color: { argb: 'FF042F2E' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0FDFA' } };
            cell.border = { top: { style: 'medium', color: { argb: 'FF0F766E' } }, bottom: { style: 'medium', color: { argb: 'FF0F766E' } } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });

        currentRow++;
        data.items.forEach((item, index) => {
            const rowNum = currentRow;
            const rowValues = [
                index + 1,
                item.name,
                parseFloat(item.sellingPrice) || 0,
                item.description || '-',
                item.selectedAggregators.join(', '),
                item.imageName || 'No Image Attached'
            ];

            rowValues.forEach((val, idx) => {
                const colLetter = String.fromCharCode(65 + idx);
                const cell = sheet.getCell(`${colLetter}${rowNum}`);
                cell.value = val;
                if (idx === 2) { // Original Price
                    cell.numFmt = '"QAR" #,##0.00'; 
                    cell.alignment = { horizontal: 'center' };
                }
            });
            currentRow++;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `MySubmission_${data.submissionId}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) { console.error(e); alert("Error generating Excel file"); }
  };

  // Unified Workflow Progress Component
  const WorkflowProgress = ({ currentStage, status }: { currentStage?: WorkflowStage, status?: SubmissionStatus }) => {
      const steps = [
          { id: WorkflowStage.F_AND_B, label: 'F&B', fullLabel: 'Food & Beverage' },
          { id: WorkflowStage.F_AND_P, label: 'F&P', fullLabel: 'Finance & Planning' },
          { id: WorkflowStage.DIGITAL_SALES, label: 'Digital', fullLabel: 'Digital Sales' }
      ];

      const isRejected = status === SubmissionStatus.REJECTED;
      const isNeedsRevision = status === SubmissionStatus.NEEDS_REVISION;
      const isCompleted = status === SubmissionStatus.APPROVED || status === SubmissionStatus.EXPORTED;

      const getCurrentStepIndex = () => {
          if (isCompleted) return steps.length;
          if (isNeedsRevision && currentStage === WorkflowStage.RETURNED) return -1; 
          const idx = steps.findIndex(s => s.id === currentStage);
          // Default to 0 if unknown stage
          return idx === -1 ? 0 : idx;
      };

      const currentIdx = getCurrentStepIndex();

      return (
          <div className="flex items-center justify-between w-full min-w-[280px] max-w-md relative pb-8">
              {/* Connecting Line Background */}
              <div className="absolute top-5 -translate-y-1/2 left-0 right-0 h-1.5 bg-zinc-100 rounded-full z-0" />
              
              {/* Active Progress Line */}
              <div 
                className={`absolute top-5 -translate-y-1/2 left-0 h-1.5 bg-teal-500 rounded-full z-0 transition-all duration-500 ease-out ${isRejected ? 'bg-red-500' : isNeedsRevision ? 'bg-amber-500' : ''}`}
                style={{ width: `${(Math.max(currentIdx, 0) / (steps.length - 1)) * 100}%` }}
              />

              {steps.map((step, index) => {
                  let state = 'waiting'; 
                  if (isRejected && index === currentIdx) state = 'error';
                  else if (isNeedsRevision && index === currentIdx) state = 'revision';
                  else if (index < currentIdx || isCompleted) state = 'completed';
                  else if (index === currentIdx) state = 'active';

                  return (
                      <div key={step.id} className="relative z-10 flex flex-col items-center group">
                          <div 
                            className={`
                                w-10 h-10 rounded-full flex items-center justify-center border-[3px] transition-all duration-300 shadow-sm
                                ${state === 'completed' ? 'bg-teal-500 border-teal-500 text-white' : 
                                  state === 'active' ? 'bg-white border-teal-500 text-teal-600 ring-4 ring-teal-50 scale-110' :
                                  state === 'error' ? 'bg-red-500 border-red-500 text-white' :
                                  state === 'revision' ? 'bg-amber-500 border-amber-500 text-white' :
                                  'bg-white border-zinc-200 text-zinc-300'}
                            `}
                          >
                              {state === 'completed' ? <Check className="w-5 h-5" strokeWidth={3} /> : 
                               state === 'active' ? <Loader2 className="w-5 h-5 animate-spin" /> :
                               state === 'error' ? <XCircle className="w-5 h-5" /> :
                               state === 'revision' ? <CornerUpLeft className="w-5 h-5" /> :
                               <span className="text-xs font-bold">{index + 1}</span>}
                          </div>
                          
                          <div className={`absolute top-12 whitespace-nowrap text-center transition-all duration-300 ${state === 'active' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}`}>
                              <span className={`text-xs font-bold px-2 py-1 rounded-md ${state === 'active' ? 'bg-teal-50 text-teal-700' : 'bg-zinc-100 text-zinc-500'}`}>
                                  {step.label}
                              </span>
                          </div>
                      </div>
                  )
              })}
          </div>
      );
  };

  const renderSubmissionCard = (sub: SubmissionData) => {
      const isNeedsRevision = sub.status === SubmissionStatus.NEEDS_REVISION;
      const isPending = sub.status === SubmissionStatus.PENDING;
      const currentStage = sub.currentStage || WorkflowStage.F_AND_B;
      
      const isLocked = 
         sub.status === SubmissionStatus.APPROVED || 
         sub.status === SubmissionStatus.EXPORTED || 
         sub.status === SubmissionStatus.REJECTED ||
         (isPending && currentStage !== WorkflowStage.F_AND_B);

      const returnLog = sub.workflowLogs
        ? sub.workflowLogs.filter(l => l.action === 'Return').slice(-1)[0] || null
        : null;
      const returnNote = isNeedsRevision && returnLog ? returnLog.comment : null;

      const formatDate = (value?: string) => {
          if (!value) return '-';
          const d = new Date(value);
          return isNaN(d.getTime()) ? value : d.toLocaleString();
      };

      return (
      <div id={`submission-${sub.submissionId}`} key={sub.submissionId} className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all overflow-hidden ${isNeedsRevision ? 'border-amber-300 ring-2 ring-amber-100' : 'border-zinc-200'}`}>
         {/* Header Row */}
         <div onClick={() => toggleExpand(sub.submissionId!)} className="p-5 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 cursor-pointer bg-white hover:bg-zinc-50/50 transition-colors group">
             {/* Info Left */}
             <div className="flex items-center gap-5 flex-1 min-w-0">
                <div className="flex flex-col gap-2">
                  <div className="bg-zinc-100 text-zinc-600 px-3 py-2 rounded-xl font-mono font-bold text-xs border border-zinc-200 shadow-sm hidden sm:block min-w-[80px] text-center shrink-0">
                      <SubmissionCode submissionId={sub.submissionId} brandName={resolveBrandName(sub)} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {renderBrandChip(sub)}
                  </div>
                </div>
                 <div className="min-w-0">
                     <div className="flex items-center gap-2 text-zinc-900 font-bold text-lg leading-tight truncate">
                         {sub.type === SubmissionType.PROPOSAL ? (
                            <span className="bg-indigo-100 text-indigo-700 p-1 rounded-md shrink-0" title="Proposal"><Lightbulb className="w-4 h-4" /></span>
                         ) : (
                            <span className="bg-teal-100 text-teal-700 p-1 rounded-md shrink-0" title="Campaign"><Tag className="w-4 h-4" /></span>
                         )}
                         {sub.campaignName || 'Campaign Submission'}
                     </div>
                     <div className="flex items-center gap-3 text-zinc-500 text-xs font-medium mt-1.5">
                         <span className="flex items-center gap-1 bg-zinc-50 px-2 py-0.5 rounded border border-zinc-100 whitespace-nowrap"><Calendar className="w-3 h-3" /> {formatDate(sub.timestamp)}</span>
                         <span className="flex items-center gap-1 bg-zinc-50 px-2 py-0.5 rounded border border-zinc-100 whitespace-nowrap"><Package className="w-3 h-3" /> {sub.items.length} Items</span>
                     </div>
                 </div>
             </div>

             {/* Workflow & Status Right */}
             <div className="flex flex-col sm:flex-row items-start sm:items-start gap-6 w-full lg:w-auto justify-between lg:justify-end pl-0 lg:pl-6 lg:border-l lg:border-zinc-100">
                 
                 <div className="flex items-start gap-4">
                     <div className="text-right hidden sm:block mt-2">
                         <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1">Progress</div>
                         <div className={`text-xs font-bold ${sub.status === SubmissionStatus.PENDING ? 'text-teal-600' : sub.status === SubmissionStatus.NEEDS_REVISION ? 'text-amber-600' : 'text-zinc-600'}`}>
                             {sub.status === SubmissionStatus.PENDING ? 'In Review' : sub.status}
                         </div>
                     </div>
                     <WorkflowProgress currentStage={sub.currentStage} status={sub.status} />
                 </div>

                 <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 ${expandedId === sub.submissionId ? 'bg-zinc-900 text-white rotate-180' : 'bg-zinc-100 text-zinc-400 group-hover:bg-white group-hover:shadow-sm'}`}>
                     <ChevronDown className="w-4 h-4" />
                 </div>
             </div>
         </div>

         {/* Expanded Details */}
         {expandedId === sub.submissionId && (
             <div className="border-t border-zinc-200 bg-white animate-fade-in">
                 {/* Return Notice */}
                 {isNeedsRevision && returnNote && (
                     <div className="mx-6 mt-6 bg-amber-50 p-4 rounded-xl border border-amber-200 flex items-start gap-3 shadow-sm">
                         <CornerUpLeft className="w-5 h-5 text-amber-600 mt-0.5" />
                         <div>
                             <h4 className="font-bold text-amber-800 text-sm">Returned for Revision</h4>
                             <p className="text-amber-700 text-sm mt-1">{returnNote}</p>
                             {returnLog && (
                               <p className="text-[10px] text-amber-600 mt-2 font-bold flex items-center gap-1">
                                   <User className="w-3 h-3" /> {returnLog.user} • {returnLog.timestamp}
                               </p>
                             )}
                         </div>
                     </div>
                 )}

                 {/* Header Actions Bar */}
                 <div className="px-6 py-4 border-b border-zinc-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-zinc-50/30">
                     <div className="flex items-center gap-2 text-zinc-900 font-bold tracking-tight">
                         <Package className="w-5 h-5 text-teal-600" />
                         <span className="uppercase text-sm">Item Details</span>
                     </div>
                     
                     <div className="flex gap-3 w-full sm:w-auto items-center">
                         {isLocked ? (
                             <span className="text-xs text-zinc-400 font-medium flex items-center gap-1 mr-2">
                                 <Lock className="w-3 h-3" /> Locked: In Review by {currentStage}
                             </span>
                         ) : (
                             <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mr-2 hidden sm:flex">
                                 <CheckCircle className="w-3 h-3" /> Editing Available
                             </span>
                         )}

                         {!isLocked && (
                             <button onClick={() => onEdit(sub)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-zinc-900 text-white px-4 py-2 rounded-lg font-bold text-xs hover:bg-zinc-800 transition-all shadow-md shadow-zinc-900/10 active:scale-95">
                                 <Edit className="w-3.5 h-3.5" /> {isNeedsRevision ? 'Fix & Resubmit' : 'Edit Submission'}
                             </button>
                         )}
                         <button onClick={() => handleDownloadExcel(sub)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border border-zinc-200 text-zinc-600 px-4 py-2 rounded-lg font-bold text-xs hover:bg-zinc-50 hover:border-zinc-300 transition-all shadow-sm">
                             <Download className="w-3.5 h-3.5" /> Excel Receipt
                         </button>
                     </div>
                 </div>
                 
                 {/* Table Container */}
                 <div className="p-0 sm:p-6">
                     <div className="overflow-hidden rounded-none sm:rounded-xl border-y sm:border border-zinc-200 shadow-sm bg-white w-full">
                         <table className="w-full text-sm text-left">
                             <thead className="text-xs text-zinc-500 uppercase bg-zinc-50/80 border-b border-zinc-200 font-bold">
                                 <tr>
                                     <th className="px-6 py-4 w-[10%] text-center">Image</th>
                                     <th className="px-6 py-4 w-[30%]">Item Name</th>
                                     <th className="px-6 py-4 w-[15%]">Original Price</th>
                                     <th className="px-6 py-4 w-[25%]">Description</th>
                                     <th className="px-6 py-4 w-[20%]">Platforms</th>
                                 </tr>
                             </thead>
                             <tbody className="divide-y divide-zinc-100">
                                 {sub.items.map((item, idx) => (
                                 <tr key={idx} className="hover:bg-zinc-50/50 transition-colors">
                                         <td className="px-6 py-4 align-middle text-center">
                                            {resolveImage(item.image || (item as any).imagePreview) ? (
                                                 <div className="w-12 h-12 rounded-lg border border-zinc-200 overflow-hidden bg-zinc-50 mx-auto shadow-sm">
                                                    <img src={resolveImage(item.image || (item as any).imagePreview) || ''} alt={item.name} className="w-full h-full object-cover" />
                                                 </div>
                                             ) : (
                                                <div className="w-12 h-12 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-300 mx-auto">
                                                    <ImageIcon className="w-5 h-5" />
                                                </div>
                                             )}
                                         </td>
                                         <td className="px-6 py-4 font-bold text-zinc-800 align-middle">
                                             <span className="block leading-tight">{item.name}</span>
                                         </td>
                                         <td className="px-6 py-4 align-middle">
                                             <span className="font-mono text-teal-700 font-bold bg-teal-50 px-2 py-1 rounded-md text-xs whitespace-nowrap border border-teal-100">
                                                 QAR {item.sellingPrice}
                                             </span>
                                         </td>
                                         <td className="px-6 py-4 text-zinc-500 text-xs align-middle leading-relaxed">
                                             {item.description || '-'}
                                         </td>
                                         <td className="px-6 py-4 align-middle">
                                             <div className="flex flex-wrap gap-1.5">
                                                 {item.selectedAggregators.length > 0 ? item.selectedAggregators.map(p => (
                                                     <span key={p} className="text-[10px] bg-zinc-100 px-2 py-1 rounded border border-zinc-200 font-semibold text-zinc-600 whitespace-nowrap">
                                                         {p}
                                                     </span>
                                                 )) : <span className="text-xs text-zinc-400 italic">None specified</span>}
                                             </div>
                                         </td>
                                     </tr>
                                 ))}
                             </tbody>
                         </table>
                     </div>
                 </div>
             </div>
        )}
     </div>
    )};

  return (
    <div className="w-full pb-20 animate-fade-in-up">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-zinc-900 tracking-tight">My Submissions</h2>
          <p className="text-zinc-500 mt-1">Welcome, {managerName}. Track your requests.</p>
        </div>
                <div className="flex gap-3">
                        {onBack && (
                                <button
                                    onClick={onBack}
                                    className="flex items-center gap-2 bg-white border border-zinc-200 text-zinc-700 px-6 py-3 rounded-xl font-bold shadow-sm hover:bg-zinc-50 transition-all active:scale-95"
                                >
                                        <CornerUpLeft className="w-5 h-5" /> Back
                                </button>
                        )}
            {onViewSubmissions && (
                <button 
                  onClick={onViewSubmissions}
                  className="flex items-center gap-2 bg-white border border-zinc-200 text-zinc-700 px-6 py-3 rounded-xl font-bold shadow-sm hover:bg-zinc-50 transition-all active:scale-95"
                >
                    <LayoutList className="w-5 h-5" /> Review Submissions
                </button>
            )}
            {canSubmit ? (
              <>
                {onNewProposal && (
                  <button onClick={onNewProposal} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-indigo-200 active:scale-95">
                      <Plus className="w-5 h-5" /> New Proposal
                  </button>
                )}
                <button onClick={onNew} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-teal-200 active:scale-95">
                    <Plus className="w-5 h-5" /> New Submission
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2 bg-zinc-100 text-zinc-400 px-6 py-3 rounded-xl font-bold cursor-not-allowed border border-zinc-200" title="You do not have permission to submit new requests.">
                  <Lock className="w-4 h-4" /> Submission Locked
              </div>
            )}
        </div>
      </div>

      {/* Controls: Search & Group Toggle */}
      <div className="bg-white p-4 rounded-2xl border border-zinc-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center mb-4">
             <div className="relative w-full md:w-96">
                 <Search className="absolute left-3 top-3 w-5 h-5 text-zinc-400" />
                 <input 
                    type="text" 
                    placeholder="Search by ID, Campaign, or Item..." 
                    className="w-full pl-10 pr-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100 focus:bg-white transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                 />
             </div>
             
             <div className="flex bg-zinc-100 p-1 rounded-xl shrink-0">
                 <button 
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                 >
                    <LayoutList className="w-4 h-4" /> List
                 </button>
                 <button 
                    onClick={() => setViewMode('month')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${viewMode === 'month' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                 >
                    <CalendarDays className="w-4 h-4" /> Group by Month
                 </button>
             </div>
      </div>

      {/* Type Toggle */}
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-zinc-100 rounded-xl p-1 inline-flex">
          <button
            onClick={() => setTypeTab('campaign')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-2 ${typeTab === 'campaign' ? 'bg-white shadow-sm text-teal-700' : 'text-zinc-500 hover:text-zinc-700'}`}
          >
            <Tag className="w-4 h-4" /> Campaigns
          </button>
          <button
            onClick={() => setTypeTab('proposal')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-2 ${typeTab === 'proposal' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-500 hover:text-zinc-700'}`}
          >
            <Lightbulb className="w-4 h-4" /> Proposals
          </button>
        </div>
      </div>

      {/* Tabs: Active vs Completed */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-zinc-100 rounded-xl p-1 inline-flex">
          <button
            onClick={() => setStatusTab('active')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${statusTab === 'active' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
          >
            Active / In Review
          </button>
          <button
            onClick={() => setStatusTab('completed')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${statusTab === 'completed' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
          >
            Completed
          </button>
          <button
            onClick={() => setStatusTab('rejected')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${statusTab === 'rejected' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
          >
            Rejected
          </button>
        </div>
      </div>

      {displayedSubmissions.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-zinc-200 shadow-sm">
           <div className="bg-zinc-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"><FileText className="w-8 h-8 text-zinc-300" /></div>
           <h3 className="text-xl font-bold text-zinc-800 mb-2">No {typeTab === 'proposal' ? 'Proposals' : 'Submissions'} Found</h3>
           <p className="text-zinc-500 mb-8">
               {searchTerm ? 'Try adjusting your search terms.' : 
                typeTab === 'proposal' ? 'Start a new proposal to see it here.' :
                'Start a new campaign submission to see it here.'}
           </p>
           {!searchTerm && (
                typeTab === 'proposal' ? (
                    onNewProposal && <button onClick={onNewProposal} className="text-indigo-600 font-bold hover:text-indigo-800 hover:underline">Start your first proposal</button>
                ) : (
                    <button onClick={onNew} className="text-teal-600 font-bold hover:text-teal-800 hover:underline">Start your first submission</button>
                )
           )}
        </div>
      ) : (
        viewMode === 'list' ? (
            <div className="space-y-4">
                {displayedSubmissions.map(renderSubmissionCard)}
            </div>
        ) : (
            <div className="space-y-10">
                {Object.entries(groupedSubmissions!).map(([dateKey, subs]) => (
                    <div key={dateKey}>
                        <div className="flex items-center gap-2 mb-4 ml-1">
                            <div className="bg-zinc-100 p-1.5 rounded-lg text-zinc-400">
                                <Calendar className="w-4 h-4" />
                            </div>
                            <h3 className="text-zinc-500 font-bold uppercase tracking-wider text-xs">{dateKey}</h3>
                            <div className="h-px bg-zinc-200 flex-1 ml-2"></div>
                        </div>
                        <div className="space-y-4">
                            {subs.map(renderSubmissionCard)}
                        </div>
                    </div>
                ))}
            </div>
        )
      )}
    </div>
  );
};
